import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { z } from "zod";
import { TOAST_MESSAGES } from "@/config/messages";
import { SEO } from "@/components/SEO";

const authSchema = z.object({
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein").max(255),
  password: z.string().min(6, "Passwort muss mindestens 6 Zeichen haben").max(128),
});

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [reauthResetDone, setReauthResetDone] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const nextParam = searchParams.get("next");
  const safeNext =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
      ? nextParam
      : "/intern";

  // OAuth/MCP-Ziele (z. B. /.lovable/oauth/consent...) niemals über das Admin-MFA-Gate leiten,
  // damit Claude/MCP-Flows unabhängig vom internen Admin-Zwei-Faktor funktionieren.
  const isOAuthTarget =
    safeNext.startsWith("/.lovable/oauth/") || safeNext.startsWith("/oauth/");

  // reauth=1 erzwingt eine Neuanmeldung mit Passwort. Für OAuth-Ziele wird das ignoriert,
  // damit Claude/MCP-Flows eine bestehende Session weiterverwenden können.
  const forceReauth = searchParams.get("reauth") === "1" && !isOAuthTarget;

  // Nur bei echten Admin-Zielen über /admin-mfa leiten; sonst direkt zum Ziel.
  const postLoginTarget = isOAuthTarget
    ? safeNext
    : `/admin-mfa?next=${encodeURIComponent(safeNext)}`;

  // Bei reauth=1: bestehende Session sauber beenden, bevor die Login-Maske erscheint.
  useEffect(() => {
    if (!forceReauth) {
      setReauthResetDone(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await supabase.auth.signOut();
      } catch {
        /* ignore */
      }
      if (!cancelled) setReauthResetDone(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [forceReauth]);

  useEffect(() => {
    if (!reauthResetDone) return;
    // Im reauth-Admin-Flow KEINE parallelen Auto-Redirects auf Basis vorhandener
    // Sessions - der einzige Redirect erfolgt explizit nach signInWithPassword.
    // Nur für OAuth/MCP-Ziele bestehende Sessions weiterverwenden.
    if (forceReauth) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        window.location.href = postLoginTarget;
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        window.location.href = postLoginTarget;
      }
    });

    return () => subscription.unsubscribe();
  }, [postLoginTarget, reauthResetDone, forceReauth]);

  const validateForm = () => {
    try {
      authSchema.parse({ email, password });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { email?: string; password?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === "email") fieldErrors.email = err.message;
          if (err.path[0] === "password") fieldErrors.password = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isForgotPassword) {
      if (!email.trim()) {
        setErrors({ email: "Bitte geben Sie Ihre E-Mail-Adresse ein" });
        return;
      }
      setIsLoading(true);
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        toast({
          title: "E-Mail gesendet",
          description: "Falls ein Konto mit dieser E-Mail existiert, erhalten Sie einen Link zum Zurücksetzen.",
        });
        setIsForgotPassword(false);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : TOAST_MESSAGES.auth.genericError.description;
        toast({ title: "Fehler", description: message, variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!validateForm()) return;
    
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            ...TOAST_MESSAGES.auth.loginFailed,
            variant: "destructive",
          });
        } else {
          throw error;
        }
      } else {
        toast(TOAST_MESSAGES.auth.loginSuccess);
        window.location.href = postLoginTarget;
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : TOAST_MESSAGES.auth.genericError.description;
      toast({
        title: TOAST_MESSAGES.auth.genericError.title,
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO title="Anmeldung" description="Interner Bereich – Anmeldung." noindex />
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>{isForgotPassword ? "Passwort zurücksetzen" : "Anmeldung"}</CardTitle>
          <CardDescription>
            {isForgotPassword
              ? "Geben Sie Ihre E-Mail-Adresse ein, um Ihr Passwort zurückzusetzen"
              : "Melden Sie sich im internen Bereich an"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  className={`pl-10 ${errors.email ? "border-destructive" : ""}`}
                  maxLength={255}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>
            {!isForgotPassword && (
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`pl-10 ${errors.password ? "border-destructive" : ""}`}
                    maxLength={128}
                    autoComplete="current-password"
                  />
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading
                ? "Laden..."
                : isForgotPassword
                ? "Link senden"
                : "Anmelden"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setIsForgotPassword(!isForgotPassword);
                setErrors({});
              }}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {isForgotPassword
                ? "Zurück zur Anmeldung"
                : "Passwort vergessen?"}
            </button>
          </div>


          <div className="mt-6 pt-4 border-t">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Startseite
            </Button>
          </div>
        </CardContent>
      </Card>
      </div>
    </>
  );
};

export default Auth;
